'use client';

import type { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { FilterResetButton } from 'src/components/cmui/filters/filter-reset-button';

import { HubScrollFade, useHubScrollEdge } from './hub-scroll-edge';
import { HubFilterGroups, type HubFilterGroup } from './hub-filter-groups';

/**
 * **조건 칩 한 줄** — 검색칸 아래에 선다(허브·Research 가 같은 문법을 쓴다).
 *
 * ```
 *   (앞 칩) [필터 갈래…]  ⟲  딸린 말                (정렬) [오른쪽 단추]
 * ```
 *
 * 🔑 앞 칩(`children` — 기간·늘 보일 칸)과 뒤(`trailing` — 정렬)는 부르는 쪽이 채운다.
 *   필터 갈래는 정의(`groups`)로 넘긴다 — 4개 이상이면 「필터 · N」 + 요약 칩으로 접힌다(`HubFilterGroups`).
 *   이 부품은 줄 모양과 「필터 초기화」 아이콘(`FilterResetButton`)·딸린 말 자리만 쥔다.
 * ⚠️ **「필터 초기화」는 걸린 것이 있을 때만 선다** — 늘 서 있으면 걸린 것이 있는지 알 수 없다.
 * ⚠️ 넓은 화면에서는 줄이 길면 **접힌다** — 잘라 내면 안 보이는 조건이 숫자를 줄인다.
 * 🚨 **폰(md 아래)에서는 접지 않고 한 줄로 옆으로 굴린다** — 접으면 칩이 서너 줄을 먹어 결과가
 *   첫 화면 밖으로 밀린다. 오른쪽에 남은 칩이 있으면 끝을 흐리게 덮어(`HubScrollFade`) 더 있다고 알린다.
 *   「필터 초기화」·오른쪽 단추는 같은 줄의 끝에 서고, 딸린 말(`note`)은 줄 **아래** 제 줄로 내려간다.
 *   실행 단추처럼 늘 보여야 하는 오른쪽 덩이는 `pinTrailing` 으로 구르는 칸 밖에 붙인다.
 */
export function HubConditionBar({
  narrowed,
  onReset,
  showCount = false,
  groups,
  trailing,
  pinTrailing = false,
  actions,
  actionsInline = false,
  note,
  children,
}: {
  /** 지금 걸린 조건 수 — 0 이면 「필터 초기화」를 세우지 않는다 */
  narrowed: number;
  onReset: () => void;
  /** 걸린 수를 툴팁에 함께 적을지(「필터 초기화 (N)」) */
  showCount?: boolean;
  /** 필터 갈래 정의 — 줄이 칩으로 세울지 접을지 정한다 */
  groups?: readonly HubFilterGroup[];
  /** 오른쪽 덩이의 맨 앞(정렬) — `actions` 와 한 덩이로 오른쪽에 선다 */
  trailing?: ReactNode;
  /** 오른쪽 덩이를 구르는 칸 밖에 붙인다 — 폰에서도 늘 보여야 하는 실행 단추(`HubRunButton`) */
  pinTrailing?: boolean;
  /** 줄 오른쪽 끝 — 내보내기 같은 단추 */
  actions?: ReactNode;
  /** 오른쪽 단추를 끝으로 밀지 않고 「필터 초기화」 바로 뒤에 붙인다 */
  actionsInline?: boolean;
  /** 줄에 딸린 흐린 말(플랜 안내 같은 것) — 넓으면 「필터 초기화」 뒤, 폰이면 줄 아래 */
  note?: ReactNode;
  children?: ReactNode;
}) {
  const edge = useHubScrollEdge(children);
  const noteNode = note ? <Typography component="div" sx={{ typography: 'uiMeta', color: 'text.disabled' }}>{note}</Typography> : null;

  const right = (trailing || actions) ? (
    <Box sx={{ ml: actionsInline ? 0 : 'auto', display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
      {trailing}
      {actions}
    </Box>
  ) : null;

  return (
    <Box data-capture="conditions">
      <Box sx={pinTrailing ? { display: 'flex', alignItems: 'center', gap: 0.5 } : undefined}>
      <Box sx={{ position: 'relative', ...(pinTrailing ? { flex: 1, minWidth: 0 } : null) }}>
        <Box sx={{ display: { md: 'none' } }}>
          <HubScrollFade show={edge.hasMoreRight} surface="default" />
        </Box>
        <Stack
          ref={edge.ref}
          onScroll={edge.onScroll}
          direction="row"
          spacing={0.5}
          alignItems="center"
          useFlexGap
          sx={{
            flexWrap: { xs: 'nowrap', md: 'wrap' },
            overflowX: { xs: 'auto', md: 'visible' },
            // 폰에서 칩이 눌려 글자가 잘리지 않게 — 줄이 굴러야지 칩이 줄면 안 된다
            '& > *': { flexShrink: { xs: 0, md: 1 } },
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {children}

          {groups && <HubFilterGroups groups={groups} narrowed={narrowed} onReset={onReset} />}

          <FilterResetButton show={narrowed > 0} count={showCount ? narrowed : undefined} onClick={onReset} />

          {noteNode && <Box sx={{ display: { xs: 'none', md: 'block' } }}>{noteNode}</Box>}

          {!pinTrailing && right}
        </Stack>
      </Box>
      {pinTrailing && right}
      </Box>

      {noteNode && <Box sx={{ display: { md: 'none' }, mt: 0.5 }}>{noteNode}</Box>}
    </Box>
  );
}
